import React from "react";
import { T, STATUS } from "../theme/tokens.js";
import { Panel, Empty, Notice, Chip } from "./ui/index.jsx";
import { siteBoard, criticalThreshold, isProgramWide } from "../lib/siteBoard.js";
import { siteNameMap, normalise } from "../lib/siteNames.js";
import { resolveSites, unmatchedSites } from "../lib/siteAlias.js";
import SiteMatcher from "./SiteMatcher.jsx";
import { useLayerRows } from "../lib/useLayerRows.js";
import { findStatusColumns } from "../lib/geo.js";
import { gateProgress } from "../lib/gates.js";
import { highRiskThreshold } from "../lib/calc.js";

/* The per-school board, in the shape the team already reads it in.
 *
 * Red, amber, green in the ordinary project management sense, with a deeper
 * red for anywhere carrying a critical. The colour is about risk and the arc
 * is about permitting progress, so a card says two things at once: how far
 * along this school is, and whether anything is wrong there. A school can be
 * green at nought per cent, which is right: nothing is wrong, the work simply
 * has not started.
 *
 * Everything is read from the registers at the moment the page draws, so
 * editing a risk or closing a permit moves the board straight away.
 */

const FILL = {
  critical: "#6E1F1B",
  high: STATUS.inProgress.ink,
  clear: STATUS.complete.ink,
};

const WORD = {
  critical: "Critical risks",
  high: "High risks",
  clear: "No high risks",
};

/* Semicircle, the way the team's own gauges read. pathLength makes the dash
   arithmetic a percentage rather than a length nobody can check. */
function Gauge({ percent }) {
  const arc = "M 12 46 A 34 34 0 0 1 88 46";
  return (
    <svg viewBox="0 0 100 52" style={{ width: "100%", display: "block" }} aria-hidden="true">
      <path d={arc} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth={11} strokeLinecap="round" />
      <path d={arc} fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth={11}
        strokeLinecap="round" pathLength={100}
        strokeDasharray={`${Math.max(0, Math.min(100, percent))} 100`} />
    </svg>
  );
}

function Card({ card, onPermits, onRisks }) {
  const r = card.risks;
  /* The review steps where the layer has them, the permits where it does not.
     Both are real measures of where a school is; the steps are the client's
     record and are available now, the permits are finer and arrive once each
     school's own list is in. */
  const g = card.gates;
  const percent = g ? g.percent : card.permits.percent;
  const caption = g
    ? `${g.done} of ${g.reported} review steps through`
    : `${card.permits.closed} of ${card.permits.total} permits closed`;
  return (
    <div
      onClick={() => onPermits(card)}
      title={`${caption}. Click for the permits.`}
      style={{
        background: FILL[card.band], color: "#fff", borderRadius: 4,
        padding: "12px 12px 10px", cursor: "pointer",
        display: "grid", gap: 6, alignContent: "start",
        border: "1px solid rgba(0,0,0,0.18)",
      }}
    >
      <div style={{
        fontSize: 12, fontWeight: 700, lineHeight: 1.3, minHeight: 32,
        overflowWrap: "break-word",
      }}>{card.name || card.site}</div>

      <Gauge percent={percent} />

      <div style={{ textAlign: "center", fontSize: 19, fontWeight: 700, marginTop: -4 }}>
        {percent}%
      </div>
      <div style={{ textAlign: "center", fontSize: 10.5, opacity: 0.85, marginTop: -4 }}>
        {caption}
      </div>
      {g && g.reported < g.total && (
        <div style={{ textAlign: "center", fontSize: 10, opacity: 0.72, marginTop: -3 }}>
          {g.total - g.reported} step{g.total - g.reported === 1 ? "" : "s"} not reported
        </div>
      )}
      {g && card.permits.total > 0 && (
        <div style={{ textAlign: "center", fontSize: 10, opacity: 0.72, marginTop: -3 }}>
          {card.permits.closed} of {card.permits.total} permits closed
        </div>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); onRisks(card); }}
        disabled={!r.total}
        title={r.total ? "Click for the risks" : "No open risks here"}
        style={{
          marginTop: 2, background: "rgba(0,0,0,0.16)", border: "none", color: "#fff",
          borderRadius: 3, padding: "5px 6px", fontSize: 10.5, lineHeight: 1.45,
          cursor: r.total ? "pointer" : "default", textAlign: "left", width: "100%",
        }}
      >
        <div style={{ opacity: 0.85 }}>{card.region || "District not recorded"}</div>
        <div style={{ fontWeight: 700 }}>
          {r.total} open risk{r.total === 1 ? "" : "s"}
          {r.total > 0 && ` · C${r.critical} H${r.high} M${r.medium} L${r.low}`}
        </div>
        {r.unrated > 0 && (
          <div style={{ opacity: 0.85 }}>{r.unrated} not yet assessed</div>
        )}
      </button>
    </div>
  );
}

export default function SiteBoard({ contract, permits, risks, siteLabel = "School",
                                    onOpen, onSaved, read }) {
  const [matching, setMatching] = React.useState(false);

  /* The layer is the authority on which schools exist and what they are
     called. It carries the code and the name together, so nothing has to be
     typed here and a school the client adds appears on its own. */
  const source = Array.isArray(contract?.map_sources) ? contract.map_sources[0] : null;
  const layer = useLayerRows(source, read);
  const aliases = source?.aliases ?? {};

  /* Rows filed against every school are already handled on their own line.
     Asking somebody which school ALL is would be asking them to answer a
     question that has no answer. */
  const rawSites = React.useMemo(
    () => [...(permits ?? []), ...(risks ?? [])]
      .filter((r) => r.site && !isProgramWide(r))
      .map((r) => r.site),
    [permits, risks]);

  const lookup = React.useMemo(
    () => siteNameMap({ columns: layer.columns, rows: layer.rows, sites: rawSites }),
    [layer.columns, layer.rows, rawSites]);

  const names = lookup?.names ?? new Map();

  /* Every row's school settled before anything is counted, so the two
     registers stop being two different programs. What a person confirmed
     beats what the app could work out. */
  const settle = React.useCallback(
    (rows = []) => rows.flatMap((r) => {
      const codes = resolveSites(r.site, { names, aliases });
      if (!codes.length) return [r];
      /* One row against two schools becomes one row on each. A permit shared
         between Charlotte Amalie and Lockhart is a permit both of them are
         waiting on, so it has to count on both cards. */
      return codes.map((code) => (codes.length > 1
        ? { ...r, site: code, _shared: codes.length }
        : { ...r, site: code }));
    }),
    [names, aliases]);

  const board = React.useMemo(
    () => siteBoard({ permits: settle(permits), risks: settle(risks), contract }),
    [permits, risks, contract, settle]);

  /* The names nothing accounts for yet, each with a suggestion beside it.
     Never applied on its own: a wrong match files somebody's permits against
     the wrong school and nobody ever sees it. */
  const toMatch = React.useMemo(
    () => (names.size ? unmatchedSites({ sites: rawSites, names, aliases }) : []),
    [rawSites, names, aliases]);

  /* The client's own review steps, in the layer's own order. */
  const gates = React.useMemo(
    () => findStatusColumns(layer.columns, layer.rows),
    [layer.columns, layer.rows]);

  const progressByCode = React.useMemo(() => {
    const out = new Map();
    if (!lookup || !gates.length) return out;
    for (const row of layer.rows) {
      const code = normalise(row[lookup.column.key]);
      if (code) out.set(code, gateProgress(row, gates.map((g) => g.key)));
    }
    return out;
  }, [layer.rows, lookup, gates]);

  const named = (card) => names.get(normalise(card.site)) ?? null;

  /* A school with nothing filed against it still gets a card. A board that
     quietly drops it reports a program smaller than the one being run. */
  const known = React.useMemo(() => {
    if (!lookup) return board.cards;
    const byCode = new Map(board.cards.map((c) => [normalise(c.site), c]));
    const empty = (code) => ({
      site: code, region: "",
      permits: { total: 0, closed: 0, open: 0, percent: 0, records: { total: [], closed: [], open: [] } },
      risks: { total: 0, critical: 0, high: 0, medium: 0, low: 0, unrated: 0,
               records: { critical: [], high: [], medium: [], low: [], total: [] } },
      band: "clear",
    });
    const rank = { critical: 0, high: 1, clear: 2 };
    return [...names.keys()]
      .map((code) => ({ ...(byCode.get(code) ?? empty(code)),
                        name: names.get(code),
                        gates: progressByCode.get(code) ?? null }))
      .sort((a, b) => rank[a.band] - rank[b.band]
        || (a.gates?.percent ?? a.permits.percent) - (b.gates?.percent ?? b.permits.percent)
        || a.name.localeCompare(b.name));
  }, [lookup, board.cards, progressByCode, names]);

  const strangers = lookup ? board.cards.filter((c) => !named(c)) : [];
  const count = (band) => known.filter((c) => c.band === band).length;
  const high = highRiskThreshold(contract);
  const critical = criticalThreshold(contract);

  if (!board.cards.length && !known.length) {
    return (
      <Panel title={`Where each ${siteLabel.toLowerCase()} is`}>
        <Empty text={`No permits or risks are filed against a ${siteLabel.toLowerCase()} yet. Import the permitting log and this board fills in.`} />
      </Panel>
    );
  }

  return (
    <Panel
      title={`Where each ${siteLabel.toLowerCase()} is`}
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {count("critical") > 0 && <Chip color={FILL.critical}>{count("critical")} critical</Chip>}
          {count("high") > 0 && <Chip color={FILL.high}>{count("high")} with highs</Chip>}
          <Chip color={FILL.clear}>{count("clear")} clear</Chip>
        </div>
      }
    >
      {matching && (
        <SiteMatcher contract={contract} source={source} items={toMatch} names={names}
          siteLabel={siteLabel} onClose={() => setMatching(false)} onSaved={onSaved} />
      )}

      {toMatch.length > 0 && (
        <button onClick={() => setMatching(true)}
          style={{
            width: "100%", textAlign: "left", cursor: "pointer", marginBottom: 12,
            background: T.chip, border: `1px solid ${T.line}`,
            borderLeft: `4px solid ${FILL.high}`, borderRadius: 4, padding: "10px 12px",
          }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>
            {toMatch.length} name{toMatch.length === 1 ? "" : "s"} in your registers
            {" "}{toMatch.length === 1 ? "does" : "do"} not match a {siteLabel.toLowerCase()} on the layer
          </div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 3, lineHeight: 1.55 }}>
            Your registers call the same {siteLabel.toLowerCase()} different things, so some of them
            appear twice below. Click to say which is which. It takes a minute
            and it is settled for good.
          </div>
        </button>
      )}

      {/* Above the cards, not on them. These belong to the program, and
          letting one of them darken every school at once would leave the
          board saying nothing about where to go. */}
      {board.programWide.total > 0 && (
        <button
          onClick={() => onOpen?.("risks", "Program-wide open risks", board.programWide.risks)}
          style={{
            width: "100%", textAlign: "left", cursor: "pointer", marginBottom: 12,
            background: T.chip, border: `1px solid ${T.line}`,
            borderLeft: `4px solid ${board.programWide.critical > 0 ? FILL.critical
              : board.programWide.high > 0 ? FILL.high : FILL.clear}`,
            borderRadius: 4, padding: "10px 12px",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>
            {board.programWide.total} program-wide open risk
            {board.programWide.total === 1 ? "" : "s"}
            {" · "}C{board.programWide.critical} H{board.programWide.high}
            {" "}M{board.programWide.medium} L{board.programWide.low}
          </div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 3, lineHeight: 1.55 }}>
            Filed against every {siteLabel.toLowerCase()} rather than one, so they are counted
            here and colour no card. Power reliability across all islands is not
            a problem at one {siteLabel.toLowerCase()}. Click to open them.
          </div>
        </button>
      )}

      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))",
      }}>
        {known.map((card) => (
          <Card key={card.site} card={card}
            onPermits={(c) => onOpen?.("permits", `${c.site} · permits`, c.permits.records.total)}
            onRisks={(c) => onOpen?.("risks", `${c.site} · open risks`, c.risks.records.total)} />
        ))}
      </div>

      {strangers.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, marginBottom: 4 }}>
            Filed against something the schools layer does not carry
          </div>
          <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 10, lineHeight: 1.6 }}>
            {strangers.length} of these. They are drawn apart rather than as
            schools, because the layer is what decides what a school is and it
            has never heard of them. Some will be spelling, some will be real
            things that are not schools, and the two need different answers.
          </div>
          <div style={{ display: "grid", gap: 10,
                        gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))" }}>
            {strangers.map((card) => (
              <Card key={card.site} card={card}
                onPermits={(c) => onOpen?.("permits", `${c.site} · permits`, c.permits.records.total)}
                onRisks={(c) => onOpen?.("risks", `${c.site} · open risks`, c.risks.records.total)} />
            ))}
          </div>
        </div>
      )}

      {layer.error && (
        <div style={{ marginTop: 12 }}>
          <Notice>
            The schools layer could not be read just now, so these are showing
            the codes the register uses rather than the names. Everything else
            on this board is unaffected. ({layer.error})
          </Notice>
        </div>
      )}

      <div style={{ fontSize: 12.5, color: T.soft, marginTop: 14, lineHeight: 1.65 }}>
        The arc is how far through the client's own review steps
        {gates.length ? ` (${gates.map((c) => c.label).join(", ")})` : ""} that
        {" "}{siteLabel.toLowerCase()} is, read live from their layer. A step
        nobody has filled in counts neither way. The colour is risk, not progress:
        {" "}<span style={{ color: FILL.critical, fontWeight: 700 }}>{WORD.critical.toLowerCase()}</span>,
        {" "}<span style={{ color: FILL.high, fontWeight: 700 }}>{WORD.high.toLowerCase()} and no criticals</span>,
        {" "}<span style={{ color: FILL.clear, fontWeight: 700 }}>neither</span>. So a
        {" "}{siteLabel.toLowerCase()} at nought per cent with nothing wrong on it is green, and one
        carrying a critical is dark whatever its progress. Click a card for its
        permits, or the risk line for its risks.
      </div>

      <div style={{ fontSize: 12, color: T.soft, marginTop: 8, lineHeight: 1.65 }}>
        Open risks only, and the bands are worked out from the numbers on each
        risk rather than read from anywhere. On this contract's scale that puts
        critical at {critical} and above, high at {high}, and the line is set on
        the contract's settings so a register scoring one to three and one
        scoring one to five are each judged on their own terms.
      </div>

      {(board.unassigned.permits.length > 0 || board.unassigned.risks.length > 0) && (
        <div style={{ marginTop: 12 }}>
          <Notice kind="error">
            {board.unassigned.permits.length > 0 && `${board.unassigned.permits.length} permit${board.unassigned.permits.length === 1 ? " is" : "s are"}`}
            {board.unassigned.permits.length > 0 && board.unassigned.risks.length > 0 && " and "}
            {board.unassigned.risks.length > 0 && `${board.unassigned.risks.length} open risk${board.unassigned.risks.length === 1 ? " is" : "s are"}`}
            {" "}filed against no {siteLabel.toLowerCase()}, so {board.unassigned.permits.length + board.unassigned.risks.length === 1 ? "it is" : "they are"} on
            no card here and nobody is watching {board.unassigned.permits.length + board.unassigned.risks.length === 1 ? "it" : "them"} on this board.
            Fill in the {siteLabel.toLowerCase()} on those rows and they appear.
          </Notice>
        </div>
      )}
    </Panel>
  );
}
